/**
 * InvitationAcceptPage.jsx — Phase 0.7.1 public invitation acceptance.
 *
 * Mounted at `/invitations/accept?token=...`. Reachable both
 * authenticated AND unauthenticated:
 *   - logged-out  → render sign-in / sign-up CTA preserving the
 *                   `returnTo` URL so the user lands back here after
 *                   completing auth
 *   - logged-in   → automatically call the shim acceptance route
 *                   and surface the canonical state (success /
 *                   expired / revoked / already accepted / wrong
 *                   email / invalid token)
 *
 * Acceptance is delegated through the additive shim
 * (POST /api/employer/v1/invitations/accept). The frontend never
 * touches the RPC directly.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';

import { acceptInvitation } from '@/lib/invitationAccept';
import { setEmployerInviteBypass } from '@/lib/employerInviteBypass';

import BrandLogo from '@/components/BrandLogo';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, MailCheck, AlertTriangle } from 'lucide-react';


/* Map the backend's condensed P0001 messages onto a small set of
 * user-facing states. The strings mirror the messages installed by
 * migration 093 (`emp_member_invitation_accept`). */
function classifyError(message) {
  const m = String(message || '').toLowerCase();
  if (m.includes('invitation not found') || m.includes('not found')) return 'not_found';
  if (m.includes('expired'))                                          return 'expired';
  if (m.includes('revoked'))                                          return 'revoked';
  if (m.includes('already accepted') || m.includes('not pending'))    return 'already_accepted';
  if (m.includes('email mismatch') || m.includes('email does not match')) return 'wrong_email';
  return 'other';
}

const STATE_COPY = {
  loading:           { title: 'Verifying your invitation…',        icon: Loader2,        tone: 'neutral'  },
  not_authenticated: { title: 'You need to sign in first',         icon: MailCheck,      tone: 'neutral'  },
  no_token:          { title: 'This invitation link is incomplete',icon: AlertTriangle,  tone: 'warn'     },
  accepting:         { title: 'Joining the organisation…',         icon: Loader2,        tone: 'neutral'  },
  success:           { title: 'Welcome to the organisation',       icon: CheckCircle2,   tone: 'success'  },
  not_found:         { title: 'Invitation not found',              icon: XCircle,        tone: 'error'    },
  expired:           { title: 'This invitation has expired',       icon: XCircle,        tone: 'error'    },
  revoked:           { title: 'This invitation has been revoked',  icon: XCircle,        tone: 'error'    },
  already_accepted:  { title: 'This invitation has already been used', icon: AlertTriangle, tone: 'warn'  },
  wrong_email:       { title: 'This invitation is for a different email', icon: XCircle, tone: 'error'   },
  other:             { title: 'We couldn\'t accept this invitation', icon: XCircle,      tone: 'error'   },
};

const TONE_CLASS = {
  neutral: 'border-zinc-200 dark:border-zinc-800',
  success: 'border-green-500/40 bg-green-50 dark:bg-green-950/20',
  warn:    'border-amber-500/40 bg-amber-500/10 dark:bg-amber-950/20',
  error:   'border-red-500/40 bg-red-50 dark:bg-red-950/20',
};


export default function InvitationAcceptPage() {
  const { user, loading: authLoading } = useAuth();
  const { reload: reloadWorkspace } = useWorkspace();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = (params.get('token') || '').trim();
  const returnTo = useMemo(
    () => `/invitations/accept?token=${encodeURIComponent(token)}`,
    [token],
  );

  /* Top-level UX state. `null` means "decide on first effect run". */
  const [state, setState]   = useState('loading');
  const [detail, setDetail] = useState('');
  const [accountId, setAccountId] = useState(null);

  /* Single-fire acceptance dispatch. Re-runs only when the auth
   * status or the token changes. */
  const runAccept = useCallback(async () => {
    setState('accepting');
    setDetail('');
    try {
      const result = await acceptInvitation({ token });
      setAccountId(result?.account_id || null);
      // Phase 0.7.2 — set the route-scoped, user-tied bypass marker
      // so `ProtectedRoute`+`WorkspaceContext` lets a fresh invitee
      // (with no candidate profile yet) reach the employer workspace.
      // Then prod the workspace context to re-fetch so its access
      // payload reflects the newly active marker before we navigate.
      if (user?.id) {
        setEmployerInviteBypass(user.id);
        try { await reloadWorkspace?.(); } catch { /* non-fatal */ }
      }
      setState('success');
    } catch (e) {
      const message = e?.message || e?.data?.detail || 'Acceptance failed';
      const kind = classifyError(message);
      setDetail(message);
      setState(kind);
    }
  }, [token, user, reloadWorkspace]);

  useEffect(() => {
    if (authLoading) return;
    if (!token) { setState('no_token'); return; }
    if (!user)  { setState('not_authenticated'); return; }
    runAccept();
  }, [authLoading, user, token, runAccept]);

  /* On success, give the user a moment to read the success
   * message, then redirect into the employer workspace. */
  useEffect(() => {
    if (state !== 'success') return undefined;
    const id = setTimeout(() => navigate('/employer/members'), 1500);
    return () => clearTimeout(id);
  }, [state, navigate]);

  const meta = STATE_COPY[state] || STATE_COPY.other;
  const Icon = meta.icon;
  const tone = TONE_CLASS[meta.tone] || TONE_CLASS.neutral;
  const isSpinning = state === 'loading' || state === 'accepting';

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-ndor-canvas p-4"
      data-testid="invitation-accept-page">
      <div className="mb-6">
        <BrandLogo />
      </div>

      <Card className={`w-full max-w-md p-6 ${tone}`} data-testid={`invitation-accept-state-${state}`}>
        <div className="flex items-center gap-3">
          <Icon className={`h-6 w-6 ${isSpinning ? 'animate-spin' : ''}`} />
          <h1 className="text-lg font-semibold" data-testid="invitation-accept-title">
            {meta.title}
          </h1>
        </div>

        {/* ── Per-state body ─────────────────────────────────────── */}

        {state === 'no_token' && (
          <p className="mt-3 text-sm text-slate-300 dark:text-zinc-300">
            The link you opened doesn't carry an invitation token. Ask the
            person who invited you to share the original email link.
          </p>
        )}

        {state === 'not_authenticated' && (
          <>
            <p className="mt-3 text-sm text-slate-300 dark:text-zinc-300">
              Sign in or create an account using the email address that
              received this invitation. We'll bring you back to finish
              joining the organisation.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Link
                to={`/login?returnTo=${encodeURIComponent(returnTo)}`}
                className="flex-1"
                data-testid="invitation-accept-signin-link"
              >
                <Button className="w-full">Sign in</Button>
              </Link>
              <Link
                to={`/signup?returnTo=${encodeURIComponent(returnTo)}`}
                className="flex-1"
                data-testid="invitation-accept-signup-link"
              >
                <Button variant="outline" className="w-full">Create an account</Button>
              </Link>
            </div>
          </>
        )}

        {state === 'success' && (
          <>
            <p className="mt-3 text-sm text-green-800 dark:text-green-200">
              You're now a member of the organisation.
              {accountId && (
                <span className="ml-1 block font-mono text-xs opacity-60">
                  account {accountId.slice(0, 8)}…
                </span>
              )}
            </p>
            <p className="mt-2 text-xs text-slate-300">
              Taking you to your member workspace…
            </p>
            <div className="mt-4">
              <Link to="/employer/members" data-testid="invitation-accept-go-now">
                <Button className="w-full">Go to Members now</Button>
              </Link>
            </div>
          </>
        )}

        {['not_found', 'expired', 'revoked', 'already_accepted', 'wrong_email', 'other'].includes(state) && (
          <>
            <p className="mt-3 text-sm text-slate-200 dark:text-zinc-300">
              {state === 'expired' && (
                'Ask the admin who invited you to send a fresh invitation.'
              )}
              {state === 'revoked' && (
                'The admin has cancelled this invitation. Ask them to send a new one if you still need access.'
              )}
              {state === 'already_accepted' && (
                'This invitation has been used already. If you can\'t reach your workspace, ask an admin to verify your membership.'
              )}
              {state === 'wrong_email' && (
                'The invitation was sent to a different email address. Sign in with the matching address, or ask the admin to issue a new invitation.'
              )}
              {state === 'not_found' && (
                'Double-check the link, or ask the admin to send a fresh invitation.'
              )}
              {state === 'other' && (
                'Please try again in a moment. If the issue persists, ask the admin to re-send the invitation.'
              )}
            </p>
            {detail && (
              <p className="mt-2 break-words text-xs text-slate-300 dark:text-slate-300"
                data-testid="invitation-accept-error-detail">
                Details: {detail}
              </p>
            )}
            <div className="mt-4">
              <Link to="/" data-testid="invitation-accept-home-link">
                <Button variant="outline" className="w-full">Back to home</Button>
              </Link>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
